import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import StatusBadge from './StatusBadge';

const COLUMNS = [
  { key: 'estado', label: 'Estado', sortable: true },
  { key: 'fecha_factura', label: 'Fecha', sortable: true },
  { key: 'numero_factura', label: 'N Factura', sortable: true },
  { key: 'proveedor_nombre', label: 'Proveedor', sortable: true },
  { key: 'base_imponible', label: 'Base', sortable: true, numeric: true },
  { key: 'iva', label: 'IVA', sortable: true, numeric: true },
  { key: 'irpf', label: 'IRPF', sortable: true, numeric: true },
  { key: 'total', label: 'Total', sortable: true, numeric: true },
  { key: 'origen', label: 'Origen', sortable: true },
];

function formatCurrency(value) {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function MobileCard({ factura, selected, onSelect, onRowClick }) {
  return (
    <div
      className="p-3 bg-white border border-gray-200 rounded-lg active:bg-gray-50 transition-colors"
      onClick={() => onRowClick(factura.id)}
    >
      <div className="flex items-start gap-3">
        <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(factura.id)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              {factura.proveedor_nombre || 'Sin proveedor'}
            </span>
            <span className="text-sm font-mono font-semibold text-gray-900 shrink-0">
              {formatCurrency(factura.total)}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <StatusBadge status={factura.estado} />
            <span className="text-xs text-gray-500">{formatDate(factura.fecha_factura)}</span>
            {factura.numero_factura && (
              <span className="text-xs text-gray-400 truncate">
                {factura.numero_factura}
              </span>
            )}
          </div>
          {factura.origen && (
            <span className="text-[10px] text-gray-400 uppercase tracking-wider mt-1 inline-block">
              {factura.origen}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FacturaTable({
  facturas = [],
  sortBy,
  sortDir,
  onSort,
  selectedIds = [],
  onSelectAll,
  onSelectOne,
  onRowClick,
}) {
  const allSelected = facturas.length > 0 && selectedIds.length === facturas.length;

  const renderSortIcon = (col) => {
    if (!col.sortable) return null;
    if (sortBy === col.key) {
      return sortDir === 'asc' ? (
        <ArrowUp className="w-3 h-3" />
      ) : (
        <ArrowDown className="w-3 h-3" />
      );
    }
    return <ArrowUpDown className="w-3 h-3 opacity-30" />;
  };

  const renderCell = (factura, col) => {
    const value = factura[col.key];
    switch (col.key) {
      case 'estado':
        return <StatusBadge status={value} />;
      case 'fecha_factura':
        return <span className="text-sm">{formatDate(value)}</span>;
      case 'base_imponible':
      case 'iva':
      case 'irpf':
      case 'total':
        return <span className="text-sm font-mono">{formatCurrency(value)}</span>;
      case 'origen':
        return (
          <span className="text-xs text-gray-500 uppercase tracking-wider">
            {value || '-'}
          </span>
        );
      default:
        return <span className="text-sm">{value || '-'}</span>;
    }
  };

  return (
    <>
      {/* Mobile card view */}
      <div className="sm:hidden space-y-2 p-3">
        {facturas.length > 0 && (
          <div className="flex items-center gap-2 pb-2" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onSelectAll}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-xs text-gray-500">Seleccionar todo</span>
          </div>
        )}
        {facturas.map((factura) => (
          <MobileCard
            key={factura.id}
            factura={factura}
            selected={selectedIds.includes(factura.id)}
            onSelect={onSelectOne}
            onRowClick={onRowClick}
          />
        ))}
        {facturas.length === 0 && (
          <div className="py-12 text-center text-gray-400">
            No se encontraron facturas
          </div>
        )}
      </div>

      {/* Desktop table view */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-3 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onSelectAll}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider ${
                    col.numeric ? 'text-right' : 'text-left'
                  } ${col.sortable ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  <div
                    className={`flex items-center gap-1 ${col.numeric ? 'justify-end' : ''}`}
                  >
                    {col.label}
                    {renderSortIcon(col)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {facturas.map((factura) => (
              <tr
                key={factura.id}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onRowClick(factura.id)}
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(factura.id)}
                    onChange={() => onSelectOne(factura.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </td>
                {COLUMNS.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-3 ${col.numeric ? 'text-right' : ''}`}
                  >
                    {renderCell(factura, col)}
                  </td>
                ))}
              </tr>
            ))}
            {facturas.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-3 py-12 text-center text-gray-400"
                >
                  No se encontraron facturas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
